import { IActivityQuestSpecs } from 'src/app/classes/def/nav-params/activity-details';
import { IQuestAction, EQuestAction, IQuestActionMapClue, IQuestActivityStatus, IQuestOptions, EQuestType } from 'src/app/classes/def/activity/quest';
import { IQuestActivityDef } from 'src/app/classes/def/core/activity';

export class QuestUtils {
    /**
     * send quest action
     * validate, check penalty
     */
    static checkAction(questData: IActivityQuestSpecs, questStatus: IQuestActivityStatus, options: IQuestOptions, action: number, apply: boolean): IQuestAction {
        let qa: IQuestAction = {
            action: null,
            data: null
        };      

        if (!questData) {
            return qa;
        }

        switch (action) {
            case EQuestAction.requireClue:
                // check available clue
                if (questData.clue) {
                    qa.action = action;
                    qa.data = questData.clue;
                    if (apply) {
                        if (!questStatus.usedClue) {
                            questStatus.usedClue = true;
                            questStatus.penalty += options.cluePenalty;
                        }
                    }
                }
                break;
            case EQuestAction.requireMapClue:
                if (questData.lat && questData.lng) {
                    let qa1: IQuestActionMapClue = qa;
                    qa1.action = action;
                    qa1.data = {
                        lat: questData.lat,
                        lng: questData.lng
                    };
                    if (apply) {
                        if (!questStatus.usedMapClue) {
                            questStatus.usedMapClue = true;
                            questStatus.penalty += options.mapCluePenalty;
                        }
                    }
                }
                break;
            default:
                break;
        }
        QuestUtils.checkPenaltyCap(questStatus);
        return qa;
    }

    static checkPenaltyCap(questStatus: IQuestActivityStatus) {
        if (questStatus) {
            if (questStatus.penalty > 100) {
                questStatus.penalty = 100;
            }
        }
    }

    static applyCumulativeRetryPenalty(questData: IActivityQuestSpecs, questStatus: IQuestActivityStatus, qad: IQuestActivityDef) {
        if (!questData) {
            return;
        }
        questStatus.penalty = QuestUtils.getCumulativeRetryPenalty(questData, questStatus, qad);
        QuestUtils.checkPenaltyCap(questStatus);
    }

    static getMaxRetry(questData: IActivityQuestSpecs, qad: IQuestActivityDef) {
        const retryPenalty = QuestUtils.getRetryPenalty(questData, qad);
        return 1 + Math.floor(100 / retryPenalty);
    }

    static getRetryPenalty(questData: IActivityQuestSpecs, qad: IQuestActivityDef) {
        if (!questData) {
            return;
        }
        let retryPenalty: number = 10;
        if (qad && (qad.maxRetry != null) && (qad.maxRetry > 0)) {
            retryPenalty = Math.floor(100 / qad.maxRetry);
        }
        switch (questData.t) {
            case EQuestType.text:
                break;
            case EQuestType.numeric:
                break;
            case EQuestType.select:
                if (questData.opt != null && questData.opt.length > 0) {
                    retryPenalty = Math.floor((100 + retryPenalty) / questData.opt.length);
                }
                break;
            case EQuestType.selectMulti:
                if (questData.opt != null && questData.opt.length > 0) {
                    retryPenalty = Math.floor((100 + retryPenalty) / questData.opt.length);
                }
                break;
            default:
                break;
        }
        return retryPenalty;
    }

    static getCumulativeRetryPenalty(questData: IActivityQuestSpecs, questStatus: IQuestActivityStatus, qad: IQuestActivityDef) {
        if (!questData) {
            return;
        }
        let retryPenalty: number = QuestUtils.getRetryPenalty(questData, qad);
        let cumulativePenalty: number = questStatus.penalty;
        cumulativePenalty += retryPenalty;

        if (cumulativePenalty > 100) {
            cumulativePenalty = 100;
        }
        return cumulativePenalty;
    }

    static getRetriesLeft(qad: IQuestActivityDef, questStatus: IQuestActivityStatus): number {
        if (!qad || qad.maxRetry == null || qad.maxRetry <= 0) {
            return 0;
        }
        const penaltyPerRetry = Math.floor(100 / qad.maxRetry);
        const retriesUsed = Math.ceil(questStatus.penalty / penaltyPerRetry);
        const retriesLeft = qad.maxRetry - retriesUsed;
        return Math.max(0, retriesLeft);
    }

    static getFeedback(qd: IActivityQuestSpecs, match: boolean, showCorrectAnswer: boolean) {
        let feedback: string = "";

        if (!qd) {
            return feedback;
        }

        if (qd.free) {
            return "<p>Your response was registered</p>";
        }

        if (!match && !showCorrectAnswer) {
            return feedback;
        }

        if (qd.opt && qd.opt.length > 0) {
            switch (qd.t) {
                case EQuestType.text:
                    if (qd.opt != null && qd.opt.length > 0) {
                        feedback += !match ? "<p>The correct answer is:</p>" : "";
                        for (let a of qd.opt) {
                            feedback += "<p>" + a + "</p>";
                        }
                    } else {
                        feedback += !match ? "<p>The correct answer is:</p>" : "";
                        feedback += "<p>" + qd.a + "</p>";
                    }
                    break;
                case EQuestType.select:
                    if (qd.a != null) {
                        feedback += !match ? "<p>The correct answer is:</p>" : "";
                        feedback += "<p>" + qd.opt[qd.a] + "</p>";
                    } else {
                        feedback += !match ? "<p>The correct answers are:</p>" : "";
                        for (let a of qd.opt) {
                            feedback += "<p>" + a + "</p>";
                        }
                    }
                    break;
                case EQuestType.selectMulti:
                    if (qd.a != null) {
                        feedback += !match ? "<p>The correct answers are:</p>" : "";
                        // valid specs defined in question params as csv string
                        let validSpecs: string[] = (qd.a as string).split(",");
                        let validSpecsInd: number[] = validSpecs.map(vs => Number.parseInt(vs));
                        for (let ind of validSpecsInd) {
                            feedback += "<p>" + qd.opt[ind] + "</p>";
                        }
                    } else {
                        feedback += !match ? "<p>The correct answers are:</p>" : "";
                        for (let a of qd.opt) {
                            feedback += "<p>" + a + "</p>";
                        }
                    }
                    break;
            }
        } else {
            feedback += !match ? "<p>The correct answer is:</p>" : "";
            feedback += "<p>" + qd.a + "</p>";
        }
        return feedback;
    }
}